import type { ReactNode } from "react";

import signInHero from "@/assets/sign-in-hero.jpg";
import { HomeIcon } from "@/components/ui/icons";
import { useCurrentActor } from "@/lib/current-actor";
import { useIsPhone } from "@/lib/use-media-query";

/**
 * Gap-fill (frontend auth UI) — the one shared shell all four new public
 * auth screens (`SignIn`, `AcceptInvitation`, `ForgotPassword`,
 * `ResetPassword`) render inside. These are the only screens in the product
 * a signed-out visitor ever sees, so they deliberately render outside
 * `AppShell` entirely (no nav rail, no bottom bar, no `AlertBell` — see
 * `main.tsx`'s router table and judgement call 1).
 *
 * Split-screen on desktop (Owner-requested, 2026-08-27): the form keeps its
 * exact original card — `rounded-lg`/`shadow-overlay`, mirroring `Dialog.
 * tsx`'s own centred-card language, completely unchanged internally — now
 * sitting in the left half of a two-column shell instead of alone on a
 * centred grey backdrop. The right half is a real photo (Owner-supplied,
 * rights-cleared for production use), not a stock-template illustration —
 * cropped from the Owner's own reference.
 *
 * Phone keeps the exact original single-column layout, unchanged — the
 * image panel is conditionally rendered (`!isPhone`), not merely
 * `hidden md:block`, so a phone visitor's browser never fetches image bytes
 * for a panel it will never show.
 *
 * `subtitle`/`belowForm` (Owner-requested, 2026-08-27, second pass): the
 * Owner's own reference also carried a subtitle and a 3-item trust strip.
 * PRODUCT.md's own recorded Brand Commitments say "No logo, no marketing
 * copy" — a real, direct tension with this request, surfaced to the Owner
 * rather than silently overridden; the Owner's call stands, but the actual
 * copy used is real and verifiable (argon2id hashing, NJ-specific alerting,
 * the plain-language principle), never the reference's own unverified
 * "bank-level encryption" claim or its non-functional "Remember me" control
 * (no persistent-session feature exists behind it). Both slots are optional
 * and only `SignIn.tsx` populates them — `AcceptInvitation`/`ForgotPassword`/
 * `ResetPassword` stay exactly as focused/task-oriented as before.
 */
export function AuthScreenLayout({
  title,
  subtitle,
  belowForm,
  children,
}: {
  title: string;
  subtitle?: string;
  belowForm?: ReactNode;
  children: ReactNode;
}) {
  const isPhone = useIsPhone();
  // Live-caught (2026-08-28): a brand-new visitor whose browser has never
  // hit this backend before — a just-invited Owner clicking their email
  // link being the real case that surfaced this — has no CSRF cookie yet.
  // `app/main.py`'s own `issue_csrf_cookie` middleware sets one on *any*
  // response, but `AcceptInvitation`/`ForgotPassword`/`ResetPassword` (unlike
  // `SignIn`, which incidentally triggers this via its own `useCurrentActor`
  // "already signed in?" check) never made a single GET before their own
  // first mutating POST — so `lib/api.ts`'s own client-side CSRF guard
  // correctly, but unhelpfully, refused to even send it ("Your session
  // could not be confirmed..."). Calling the same hook here, in the one
  // shell every public auth screen already renders through, seeds the
  // cookie on mount for all four screens at once — its `data`/`error` are
  // deliberately unused here; screens that need to act on "already signed
  // in" (`SignIn` alone, today) still call it themselves for that.
  useCurrentActor();
  return (
    <div className="flex h-screen bg-surface-sunken md:bg-canvas">
      <div className="flex flex-1 items-center justify-center overflow-y-auto px-4 py-12">
        <div className="w-full max-w-sm rounded-lg bg-canvas p-6 shadow-overlay md:shadow-none">
          <div className="flex items-center gap-2">
            <HomeIcon className="text-accent" width={24} height={24} />
            {/* `ink-secondary` (6.0:1), not `ink-tertiary`: DESIGN.md's own
             * rule for ink-tertiary is explicit — "Never a value, never a
             * label, never anything a user must read to complete a task." A
             * live contrast scan measured this eyebrow at 3.1:1 against a
             * 4.5:1 requirement while it used ink-tertiary; ink-secondary is
             * the token the spec actually names for labels. */}
            <p className="text-label font-medium text-ink-secondary">PropDesk</p>
          </div>
          <p className="mt-1 text-meta font-medium text-ink-secondary">For New Jersey rental properties</p>
          <h1 className="mt-4 text-title font-semibold tracking-title text-ink">{title}</h1>
          {subtitle ? <p className="mt-1 text-body text-ink-secondary">{subtitle}</p> : null}
          <div className="mt-6 flex flex-col gap-4">{children}</div>
          {belowForm ? <div className="mt-8 border-t border-border-hairline pt-6">{belowForm}</div> : null}
        </div>
      </div>
      {!isPhone ? (
        <div className="w-1/2">
          <img src={signInHero} alt="" className="h-full w-full object-cover" />
        </div>
      ) : null}
    </div>
  );
}
